import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { renderHighlightedText } from "src/utils/searchHighlight";
import DocumentListRowIcon from "./DocumentListRowIcon";

type TooltipCoords = { left: number; top: number };

type DocumentSnippetIconTooltipProps = {
  highlightedText?: string;
  foundBy?: string;
  children?: ReactNode;
};

const HIDE_DELAY_MS = 120;

const DocumentSnippetIconTooltip = ({
  highlightedText,
  foundBy,
  children,
}: DocumentSnippetIconTooltipProps) => {

  const anchorRef = useRef<HTMLSpanElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current === null) {
      return;
    }
    clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = null;
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setCoords(null);
      hideTimeoutRef.current = null;
    }, HIDE_DELAY_MS);
  }, [clearHideTimeout]);

  const handleAnchorMouseEnter = useCallback(() => {
    clearHideTimeout();
    const el = anchorRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    setCoords({
      left: rect.left - 8,
      top: rect.top + rect.height / 2,
    });
  }, [clearHideTimeout]);

  const handleAnchorMouseLeave = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);

  const handleTooltipMouseEnter = useCallback(() => {
    clearHideTimeout();
  }, [clearHideTimeout]);

  const handleTooltipMouseLeave = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);

  const isOpen = coords !== null;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: PointerEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      // Клик внутри иконки — не закрываем здесь
      if (anchorRef.current?.contains(target)) {
        return;
      }

      // Клик внутри tooltip — тоже не закрываем
      const tooltip = document.querySelector('[role="tooltip"]');

      if (tooltip?.contains(target)) {
        return;
      }

      clearHideTimeout();
      setCoords(null);
    };

    document.addEventListener("pointerdown", handleOutsideClick);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, [isOpen, clearHideTimeout]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleSync = () => {
      const el = anchorRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      setCoords({
        left: rect.left - 8,
        top: rect.top + rect.height / 2,
      });
    };
    window.addEventListener("scroll", handleSync, true);
    window.addEventListener("resize", handleSync);
    return () => {
      window.removeEventListener("scroll", handleSync, true);
      window.removeEventListener("resize", handleSync);
    };
  }, [isOpen]);

  useEffect(
    () => () => {
      clearHideTimeout();
    },
    [clearHideTimeout]
  );
  const hideTooltip = !foundBy || foundBy !== "content" && foundBy !== "both";

  if (hideTooltip) {
    return (
      <span style={{ visibility: "hidden" as const }} className="shrink-0 pt-0.5 inline-flex" aria-hidden>
        {children ?? <DocumentListRowIcon />}
      </span>
    );
  }

  const tooltipNode =
    coords !== null && typeof document !== "undefined" ? (
      <div
        className="fixed z-[100] w-[230px] max-w-md min-w-0 rounded-[5px] bg-white p-3 shadow-[0_1px_3px_0_rgba(166,175,195,0.40)] pointer-events-auto"
        style={{
          left: coords.left,
          top: coords.top,
        transform: "translate(-100%, -50%)",
        }}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
        role="tooltip"
      >
        <div className="absolute top-1/2 right-0 translate-x-full -translate-y-1/2 w-0 h-0 border-y-[5px] border-y-transparent border-l-[8px] border-l-white drop-shadow-[1px_0_1px_rgba(166,175,195,0.3)]" />
        <div className="line-clamp-3 text-black text-sm font-normal leading-[18px] break-words">
          {renderHighlightedText(highlightedText || "")}
        </div>
      </div>
    ) : null;


  return (
    <>
      <span
        ref={anchorRef}
        className="relative inline-flex shrink-0 pt-0.5 cursor-default"
        onClick={(e) => {
            e.stopPropagation();
            if (isOpen) {
              clearHideTimeout();
              setCoords(null);
            } else {
              handleAnchorMouseEnter();
            }
        }}
      >
        {children ?? <DocumentListRowIcon />}
      </span>
      {tooltipNode !== null
        ? createPortal(tooltipNode, document.body)
        : null}
    </>
  );
};

export default DocumentSnippetIconTooltip;
